import { SceneObject } from './SceneObject';
import { SceneObjectType } from './SceneObjectType';
import { Vector2 } from './Vector2';
import { Color4 } from './Color4';
import { Rect } from './Rect';
import { SpriteBatch } from './SpriteBatch';
import { RenderDevice } from './RenderDevice';
import { Matrix3 } from './Matrix3';
import { Blend } from './Blend';
import { Effect } from './Effect';
import { RenderSupport } from './RenderSupport';
import { TextSceneObject } from './TextSceneObject';
import { TtfTextSceneObject } from './TtfTextSceneObject';
import { GlowEffect, ShadowEffect, ColorEffect } from './TextRenderParameters';
import { HorizontalAlignment } from './HorizontalAlignment';
import { VerticalAlignment } from './VerticalAlignment';

export class GlowSceneObject extends SceneObject {
  private _glowColor: Color4 = new Color4(0, 0, 0, 1);
  private _alphaMultiplier: number = 1.0;
  private _coordinateSystem: Rect = new Rect(Vector2.Zero, Vector2.One);
  private _stretchMode: string = 'Fill';
  private _renderDevice: RenderDevice;
  private _renderSupport: RenderSupport;
  private _blurWidth: number = 0;
  private _blurHeight: number = 0;
  private _glowStrength: number = 0;
  private _origin: Vector2 = Vector2.Zero;
  private _glowEffect: GlowEffect | null = null;
  private _shadowEffect: ShadowEffect | null = null;
  private _colorEffect: ColorEffect | null = null;
  private _childsText: TextSceneObject[] = [];
  private _effectApplied: boolean = false;
  private _targetSize: Vector2 | null = null;
  private _blurTargetSize: Vector2 | null = null;
  private _radius: number = 0;
  private _multiplier: number = 1;

  get runtimeType(): SceneObjectType {
    return SceneObjectType.Glow;
  }

  get glowEffect(): GlowEffect | null {
    return this._glowEffect;
  }
  set glowEffect(value: GlowEffect | null) {
    this._glowEffect = value;
  }

  get colorEffect(): ColorEffect | null {
    return this._colorEffect;
  }
  set colorEffect(value: ColorEffect | null) {
    this._colorEffect = value;
  }

  get shadowEffect(): ShadowEffect | null {
    return this._shadowEffect;
  }
  set shadowEffect(value: ShadowEffect | null) {
    this._shadowEffect = value;
  }

  get blurWidth(): number {
    return this._blurWidth;
  }
  set blurWidth(value: number) {
    this._blurWidth = value;
  }

  get blurHeight(): number {
    return this._blurHeight;
  }
  set blurHeight(value: number) {
    this._blurHeight = value;
  }

  get glowStrength(): number {
    return this._glowStrength;
  }
  set glowStrength(value: number) {
    this._glowStrength = value;
  }

  get origin(): Vector2 {
    return this._origin;
  }
  set origin(value: Vector2) {
    this._origin = value;
  }

  get targetSize(): Vector2 | null {
    return this._targetSize;
  }
  set targetSize(value: Vector2 | null) {
    this._targetSize = value;
  }

  get blurTargetSize(): Vector2 | null {
    return this._blurTargetSize;
  }
  set blurTargetSize(value: Vector2 | null) {
    this._blurTargetSize = value;
  }

  get glowColor(): Color4 {
    return this._glowColor;
  }
  set glowColor(value: Color4) {
    this._glowColor = value;
  }

  get alphaMultiplier(): number {
    return this._alphaMultiplier;
  }
  set alphaMultiplier(value: number) {
    this._alphaMultiplier = value;
  }

  get coordinateSystem(): Rect {
    return this._coordinateSystem;
  }
  set coordinateSystem(value: Rect) {
    this._coordinateSystem = value;
  }

  get stretchMode(): string {
    return this._stretchMode;
  }
  set stretchMode(value: string) {
    this._stretchMode = value;
  }

  constructor(renderDevice: RenderDevice, renderSupport: RenderSupport) {
    super();
    this._renderDevice = renderDevice;
    this._renderSupport = renderSupport;

  }

  initialize(): void {
    super.initialize();

    if (!this.id) {
      return;
    }
    console.log('GlowSceneObject constructor:', this.id, "origin", this.origin);

    if (this.id.startsWith('glow_')) {
      this.glowEffect = this.createGlowEffect();
    } else if (this.id.startsWith('shadow_')) {
      this.shadowEffect = this.createShadowEffect();
    }

    this.colorEffect = this.createColorEffect();
    // Check recursively if there are other GlowSceneObjects
    const hasGlowChild = this.findAllByType(GlowSceneObject);
    if (hasGlowChild.length > 1) {
      return;
    }

    // If no glow children, look for TextSceneObjects
    this._childsText = this.findAllByType(TextSceneObject);
  }

  createColorEffect(): ColorEffect | null {
    let color: Color4 | null = null;
    const childs = this.findAllByType(SceneObject);
    childs.forEach((child) => {
      if (child.color) {
        color = child.color;
        child.color = null;
      }
    });

    if (color) {
      const c = color as Color4;
      const effect = new ColorEffect();

      effect.color = new Color4(c.r * 255, c.g * 255, c.b * 255, c.a);
      return effect;
    }
    return null;
  }

  fillParentGlows(paretGlows: GlowSceneObject[], element: SceneObject): void {
    if (element.parent) {
      this.fillParentGlows(paretGlows, element.parent);
    }

    if (element instanceof GlowSceneObject) {
      paretGlows.push(element);
    }
  }

  createGlowEffect(): GlowEffect {
    const effect = new GlowEffect();
    effect.color = this.glowColor;
    
    // Enhanced glow calculation based on the HTML renderer approach
    const baseWidth = this.blurWidth * this.glowStrength * 0.1;
    const baseHeight = this.blurHeight * this.glowStrength * 0.1;
    
    // Use the larger dimension for consistent glow
    const maxDimension = Math.max(baseWidth, baseHeight);
    effect.width = maxDimension;
    effect.height = maxDimension;
    effect.stroke = this.origin.x * this.glowStrength * 0.1;
    
    return effect;
  }

  createShadowEffect(): ShadowEffect {
    const effect = new ShadowEffect();
    
    // Enhanced shadow calculation with better offset handling
    const offsetX = this.blurWidth * this.glowStrength * 0.1;
    const offsetY = this.blurHeight * this.glowStrength * 0.1;
    
    // Ensure minimum offset for visibility
    effect.offsetX = Math.max(offsetX, 2);
    effect.offsetY = Math.max(offsetY, 2);
    effect.color = this.glowColor;
    
    return effect;
  }

  /**
   * Creates an enhanced glow effect with multiple layers
   * Similar to the HTML renderer's approach
   */
  createEnhancedGlowEffect(): GlowEffect {
    const effect = new GlowEffect();
    effect.color = this.glowColor;
    
    // Calculate glow dimensions based on strength and blur
    const glowIntensity = this.glowStrength * 0.15; // Increased intensity
    const baseWidth = this.blurWidth * glowIntensity;
    const baseHeight = this.blurHeight * glowIntensity;
    
    // Use proportional sizing for better visual quality
    effect.width = Math.max(baseWidth, 1);
    effect.height = Math.max(baseHeight, 1);
    effect.stroke = this.origin.x * glowIntensity;
    
    return effect;
  }

  /**
   * Creates an enhanced shadow effect with better depth
   */
  createEnhancedShadowEffect(): ShadowEffect {
    const effect = new ShadowEffect();
    
    // Enhanced shadow calculation with depth
    const shadowIntensity = this.glowStrength * 0.12;
    const offsetX = this.blurWidth * shadowIntensity;
    const offsetY = this.blurHeight * shadowIntensity;
    
    // Ensure minimum offset and add some depth
    effect.offsetX = Math.max(offsetX, 3);
    effect.offsetY = Math.max(offsetY, 3);
    effect.color = this.glowColor;
    
    return effect;
  }

  draw(spriteBatch: SpriteBatch): void {
    if (this._childsText.length > 0 && !this._effectApplied) {
      this._effectApplied = true;

      const paretGlows: GlowSceneObject[] = [];
      this.fillParentGlows(paretGlows, this);

      let glowEffect: GlowEffect | null = null;
      let shadowEffect: ShadowEffect | null = null;
      paretGlows.forEach((glow) => {
        if (glow.glowEffect) {
          glowEffect = glow.glowEffect;
        }
        if (glow.shadowEffect) {
          shadowEffect = glow.shadowEffect;
        }
      });

      this._childsText.forEach((textObject) => {
        if (textObject instanceof TtfTextSceneObject) {
          if (glowEffect) {
            textObject.addGlowEffect(glowEffect);
          }
          if (shadowEffect) {
            textObject.addShadowEffect(shadowEffect);
          }
          if (this.colorEffect) {
            textObject.addColorEffect(this.colorEffect);
          }
        }
      });
    }
    super.draw(spriteBatch);
  }

  calculateWorldTransform(parentTransform: Matrix3, result: Matrix3): void {
    super.calculateWorldTransform(parentTransform, result);
    
    // Apply coordinate system offset if specified
    if (this.coordinateSystem && !this.coordinateSystem.isEmpty) {
      result.tx -= this.coordinateSystem.lt.x;
      result.ty -= this.coordinateSystem.lt.y;
    }
  }

  readProperties(properties: Record<string, any>): void {
    if (properties) {
      if (properties.GlowColor) {
        this.glowColor = new Color4(
          properties.GlowColor.r ?? 255,
          properties.GlowColor.g ?? 255,
          properties.GlowColor.b ?? 255,
          properties.GlowColor.a ?? 1
        );
      }

      if (properties.BlurWidth !== undefined) {
        this.blurWidth = properties.BlurWidth;
      }

      if (properties.BlurHeight !== undefined) {
        this.blurHeight = properties.BlurHeight;
      }

      if (properties.GlowStrength !== undefined) {
        this.glowStrength = properties.GlowStrength;
      }

      if (properties.TargetSize) {
        this.targetSize = new Vector2(properties.TargetSize.w ?? 0, properties.TargetSize.h ?? 0);
      }

      if (properties.BlurTargetSize) {
        this.blurTargetSize = new Vector2(
          properties.BlurTargetSize.w ?? 0,
          properties.BlurTargetSize.h ?? 0
        );
      }

      if (properties.CoordinateSystem) {
        this.coordinateSystem = new Rect(
          new Vector2(properties.CoordinateSystem.x ?? 0, properties.CoordinateSystem.y ?? 0),
          new Vector2(properties.CoordinateSystem.w ?? 0, properties.CoordinateSystem.h ?? 0)
        );
      }

      if (properties.Premultiplied !== undefined) {
        // TODO: Implement premultiplied property handling
        // this.premultiplied = properties.Premultiplied;
      }

      if (properties.StretchMode) {
        this.stretchMode = properties.StretchMode;
      }

      if (properties.StretchDirection) {
        // TODO: Implement stretch direction property handling
        // this.stretchDirection = properties.StretchDirection;
      }

      if (properties.Size) {
        // TODO: Implement size property handling
        this.size = new Vector2(properties.Size.w ?? 0, properties.Size.h ?? 0);
      }

      if (properties.Radius !== undefined) {
        // TODO: Implement radius property handling
        this._radius = properties.Radius;
      }

      if (properties.Multiplier !== undefined) {
        // TODO: Implement multiplier property handling
        this._multiplier = properties.Multiplier;
      }

      if (properties.GlowWidth !== undefined) {
        // Map GlowWidth to blurWidth for compatibility
        this.blurWidth = properties.GlowWidth;
      }

      if (properties.AlphaMultiplier !== undefined) {
        this.alphaMultiplier = properties.AlphaMultiplier;
      }
    }
  }
}
